import React, { useEffect, useState } from "react";
import { MdClose, MdOutlineCheckCircle } from "react-icons/md";
import { createPortal } from "react-dom";
import { useClickOutside } from './../../Hooks/useClickOutside';

const ConfirmActionModal = ({
  open,
  onClose,
  onConfirm,
  title = "Confirm Action",
  description = "Are you sure you want to proceed?",
  confirmText = "Confirm",
  loading = false,

  checkboxRequired = false,
  checkboxLabel = "",
  checkboxChecked = false,
  setCheckboxChecked = () => { },
}) => {
  const [animate, setAnimate] = useState(false);

  const handleClose = () => {
    if (loading) return;
    setAnimate(false);
    setTimeout(onClose, 250);
  };

  const domNode = useClickOutside(() => handleClose());

  useEffect(() => {
    if (open) setTimeout(() => setAnimate(true), 10);
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className={`fixed inset-0 z-50 flex items-end md:items-center justify-center
        bg-black/30 backdrop-blur-sm transition-opacity duration-300
        ${animate ? "opacity-100" : "opacity-0"}`}
    >
      <div
        ref={domNode}
        className={`w-full md:w-[520px] bg-white shadow-2xl md:rounded-3xl rounded-t-3xl
          transition-all duration-300
          ${animate ? "scale-100 translate-y-0" : "scale-95 md:translate-y-4 translate-y-full"}`}
      >
        {/* HEADER */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-green-500 via-emerald-500 to-green-600 md:rounded-t-3xl rounded-t-3xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <MdOutlineCheckCircle className="text-white" size={22} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">{title}</h2>
              <p className="text-xs text-green-100">Please confirm your action</p>
            </div>
          </div>

          <button
            onClick={handleClose}
            disabled={loading}
            className="p-2 bg-white rounded-full hover:bg-green-100 transition disabled:opacity-50"
          >
            <MdClose size={20} className="text-green-600" />
          </button>
        </div>

        {/* BODY */}
        <div className="px-6 py-6 text-sm text-gray-600 leading-relaxed space-y-5">
          <div>
            {description}
          </div>

          {checkboxRequired && (
            <label className="flex items-start gap-3 p-4 rounded-2xl border border-green-200 bg-green-50 cursor-pointer">
              <input
                type="checkbox"
                checked={checkboxChecked}
                onChange={(e) => setCheckboxChecked(e.target.checked)}
                className="mt-1 w-4 h-4 accent-green-600"
              />

              <span className="text-sm text-green-800 leading-relaxed font-medium">
                {checkboxLabel}
              </span>
            </label>
          )}
        </div>

        {/* FOOTER */}
        <div className="flex gap-3 px-5 py-5 border-t">
          <button
            onClick={handleClose}
            disabled={loading}
            className="flex-1 py-3.5 border-2 border-gray-300 text-gray-700 rounded-2xl font-medium hover:bg-gray-50 transition disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            onClick={onConfirm}
            disabled={
              loading ||
              (checkboxRequired && !checkboxChecked)
            }
            className="flex-1 py-3.5 bg-gradient-to-r from-green-500 to-emerald-600
              text-white font-semibold rounded-2xl transition-all duration-300
              hover:shadow-[0px_6px_16px_rgba(16,185,129,0.4)] disabled:opacity-70 flex items-center justify-center gap-2"
          >
            {loading && (
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8z"
                />
              </svg>
            )}
            {loading ? "Processing..." : confirmText}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ConfirmActionModal;